/**
 * BatchProgress — subscribes to the SSE stream for a batch and renders
 * aggregate + per-file progress.
 *
 * EventSource lifecycle:
 *   - Opens on mount (or when batchId changes)
 *   - Closed on unmount or when all items reach terminal state (ready/failed)
 *
 * Terminal states: ready | failed
 * In-progress states: queued | extracting | ocr-retry
 *
 * The component is intentionally read-only — it only reflects server state.
 * Actions (retry, dismiss) are emitted via callbacks to the parent.
 */

import { useEffect, useReducer, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, XCircle, Loader2, RefreshCw } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { FileStatusPill } from './FileStatusPill'
import { ingestApiClient } from '../ingest-api-client'
import type { BatchItemEvent, BatchItemStatus } from '../ingest-api-client'

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

interface FileEntry {
  uploadId: string
  filename: string
  status: BatchItemStatus
  error?: string
}

interface BatchState {
  files: Record<string, FileEntry>
  /** Ordered list of uploadIds for stable rendering */
  order: string[]
  streamError: string | null
  streamClosed: boolean
}

type BatchAction =
  | { type: 'item_event'; event: BatchItemEvent }
  | { type: 'stream_error'; message: string }
  | { type: 'stream_closed' }

const TERMINAL: Set<BatchItemStatus> = new Set(['ready', 'failed'])

function batchReducer(state: BatchState, action: BatchAction): BatchState {
  switch (action.type) {
    case 'item_event': {
      const { uploadId, filename, status, error } = action.event
      const existing = state.files[uploadId]
      return {
        ...state,
        files: {
          ...state.files,
          [uploadId]: { uploadId, filename, status, error },
        },
        order: existing ? state.order : [...state.order, uploadId],
      }
    }
    case 'stream_error':
      return { ...state, streamError: action.message, streamClosed: true }
    case 'stream_closed':
      return { ...state, streamClosed: true }
    default:
      return state
  }
}

const initialState: BatchState = {
  files: {},
  order: [],
  streamError: null,
  streamClosed: false,
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface BatchProgressProps {
  batchId: string
  /** Total expected file count — used for progress bar before events arrive */
  expectedCount: number
  isDark?: boolean
  onComplete?: (counts: { ready: number; failed: number }) => void
}

export function BatchProgress({
  batchId,
  expectedCount,
  isDark = false,
  onComplete,
}: BatchProgressProps) {
  const { t } = useTranslation('ingest')
  const [state, dispatch] = useReducer(batchReducer, initialState)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  // Hydrate from REST on mount — without this, refreshing the page or
  // navigating in late shows a stale "1 of N" because SSE only delivers
  // events from subscription time forward, not historical ones.
  useEffect(() => {
    if (!batchId) return
    let cancelled = false
    void ingestApiClient.getBatchItems(batchId, { pageSize: 100 }).then((res) => {
      if (cancelled) return
      for (const raw of res.items) {
        const item = raw as {
          id?: string
          uploadId?: string
          filename?: string
          status?: string
          errorClass?: string | null
        }
        if (!item.uploadId || !item.filename || !item.status) continue
        // Backend statuses include 'failed-<errorClass>' — normalize to the
        // 5-state union the frontend uses, surfacing errorClass separately.
        const rawStatus = item.status
        const normalized: BatchItemStatus = rawStatus.startsWith('failed-')
          ? 'failed'
          : (rawStatus as BatchItemStatus)
        dispatch({
          type: 'item_event',
          event: {
            uploadId: item.uploadId,
            filename: item.filename,
            status: normalized,
            error: item.errorClass ?? undefined,
          },
        })
      }
    }).catch(() => {
      // Hydration failure is non-fatal — SSE will still deliver future events.
    })
    return () => { cancelled = true }
  }, [batchId])

  // Open SSE stream
  useEffect(() => {
    if (!batchId) return

    const source = ingestApiClient.openBatchStream(batchId)

    const ITEM_EVENTS: BatchItemStatus[] = [
      'queued', 'extracting', 'ocr-retry', 'ready', 'failed',
    ]

    ITEM_EVENTS.forEach((eventType) => {
      source.addEventListener(`item.${eventType}`, (e: Event) => {
        try {
          // Backend SSE frame shape: { type, itemId, payload: { uploadId, filename, errorClass? } }
          // Earlier code assumed a flat shape and silently dropped events because
          // `uploadId` was undefined — every retry event then created a phantom row
          // and counters jumped to "0 of 6" for a 5-file batch.
          const raw = JSON.parse((e as MessageEvent).data) as {
            type?: string
            itemId?: string
            payload?: { uploadId?: string; filename?: string; errorClass?: string }
          }
          const uploadId = raw.payload?.uploadId
          const filename = raw.payload?.filename
          if (!uploadId || !filename) return
          dispatch({
            type: 'item_event',
            event: {
              uploadId,
              filename,
              status: eventType,
              error: raw.payload?.errorClass,
            },
          })
        } catch {
          // Malformed SSE payload — skip silently
        }
      })
    })

    source.onerror = () => {
      dispatch({ type: 'stream_error', message: t('errors.generic') })
      source.close()
    }

    return () => {
      source.close()
    }
  }, [batchId, t])

  // Detect completion and notify parent
  useEffect(() => {
    const fileList = state.order.map((id) => state.files[id]).filter(Boolean)
    const total = fileList.length
    if (total === 0 || total < expectedCount) return

    const allTerminal = fileList.every((f) => TERMINAL.has(f.status))
    if (!allTerminal) return

    const ready = fileList.filter((f) => f.status === 'ready').length
    const failed = fileList.filter((f) => f.status === 'failed').length
    onCompleteRef.current?.({ ready, failed })
  }, [state.files, state.order, expectedCount])

  const fileList = state.order.map((id) => state.files[id]).filter(Boolean)
  const doneCount = fileList.filter((f) => TERMINAL.has(f.status)).length
  const readyCount = fileList.filter((f) => f.status === 'ready').length
  const failedCount = fileList.filter((f) => f.status === 'failed').length
  const total = Math.max(fileList.length, expectedCount)
  const progressPct = total > 0 ? Math.round((doneCount / total) * 100) : 0
  const allDone = doneCount >= total && total > 0

  const retryStream = useCallback(() => {
    // Re-mount by navigating away; parent can reset batchId if needed.
    // Here we just clear local error to allow the parent to re-trigger.
    dispatch({ type: 'stream_closed' })
  }, [])

  return (
    <div className="space-y-4" aria-label={t('status.title')}>
      {/* Header row */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className={cn('text-sm font-semibold', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
            {t('status.title')}
          </h3>
          <p className={cn('text-xs mt-0.5', isDark ? 'text-neutral-500' : 'text-neutral-500')}>
            {t('status.batch_id', { id: batchId })}
          </p>
        </div>

        {/* Aggregate status icon */}
        {allDone ? (
          failedCount > 0 ? (
            <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" aria-hidden="true" />
          ) : (
            <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" aria-hidden="true" />
          )
        ) : (
          <Loader2 className="h-5 w-5 text-brand-400 animate-spin flex-shrink-0" aria-hidden="true" />
        )}
      </div>

      {/* Progress bar */}
      <div
        role="progressbar"
        aria-valuenow={progressPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('status.overall_progress', { done: doneCount, total })}
        className={cn('h-1.5 w-full overflow-hidden rounded-full', isDark ? 'bg-surface-4' : 'bg-neutral-200')}
      >
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            allDone && failedCount > 0
              ? 'bg-amber-400'
              : allDone
                ? 'bg-emerald-400'
                : 'bg-brand-500',
          )}
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Summary line */}
      <p className={cn('text-xs', isDark ? 'text-neutral-500' : 'text-neutral-500')}>
        {t('status.overall_progress', { done: doneCount, total })}
        {allDone && failedCount > 0 && (
          <span className="ml-2 text-red-400">
            ({failedCount} {failedCount === 1 ? 'failed' : 'failed'})
          </span>
        )}
      </p>

      {/* Stream error banner */}
      {state.streamError && (
        <div
          role="alert"
          className={cn(
            'flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs',
            isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700',
          )}
        >
          <span>{state.streamError}</span>
          <button
            type="button"
            onClick={retryStream}
            className="flex items-center gap-1 underline underline-offset-2"
          >
            <RefreshCw className="h-3 w-3" aria-hidden="true" />
            {t('status.retry')}
          </button>
        </div>
      )}

      {/* Per-file list */}
      {fileList.length > 0 && (
        <ul
          aria-label="File processing status"
          className="space-y-1.5"
        >
          {fileList.map((file) => (
            <li
              key={file.uploadId}
              className={cn(
                'flex items-center justify-between gap-3 rounded-lg px-3 py-2',
                isDark ? 'bg-surface-2' : 'bg-neutral-50',
              )}
            >
              <span
                className={cn(
                  'min-w-0 truncate text-xs',
                  isDark ? 'text-neutral-300' : 'text-neutral-700',
                )}
                title={file.filename}
              >
                {file.filename}
              </span>

              <FileStatusPill
                status={file.status}
                isDark={isDark}
                className="flex-shrink-0"
              />
            </li>
          ))}
        </ul>
      )}

      {/* Skeleton rows while waiting for first events */}
      {fileList.length === 0 && expectedCount > 0 && !state.streamError && (
        <ul aria-label="Waiting for progress" className="space-y-1.5">
          {Array.from({ length: Math.min(expectedCount, 5) }).map((_, i) => (
            <li
              key={i}
              className={cn(
                'h-9 animate-pulse rounded-lg',
                isDark ? 'bg-surface-3' : 'bg-neutral-100',
              )}
              aria-hidden="true"
            />
          ))}
        </ul>
      )}
    </div>
  )
}
